import { css } from '@compiled/react';

import { token } from '@atlaskit/tokens';

import { Image } from '@/libraries/design/components/image/src/image';
import type { ImageProps } from '@/libraries/design/components/image/src/image';
import { wacToken } from '@/libraries/design/foundations';

export type BlurredBackgroundImageProps = ImageProps;

export const BlurredBackgroundImage = ({
	src,
	alt,
	caption,
	className,
	style,
	contentType,
	maxPossibleWidth,
	...rest
}: BlurredBackgroundImageProps) => {
	if (!src) {
		return null;
	}

	const image = (
		<div className={className} style={style} css={imageWrapperStyles}>
			<div css={backgroundImageStyles} aria-hidden="true">
				<Image src={src} alt="" contentType={contentType} maxPossibleWidth={maxPossibleWidth} />
			</div>
			<div css={foregroundImageFrameStyles}>
				<Image
					{...rest}
					src={src}
					alt={alt}
					contentType={contentType}
					maxPossibleWidth={maxPossibleWidth}
				/>
			</div>
		</div>
	);

	return caption ? (
		<figure
			css={css({
				margin: token('space.0'),
			})}
		>
			{image}
			<figcaption
				className="body-sm"
				css={css({
					marginBlockStart: token('space.100'),
				})}
			>
				{caption}
			</figcaption>
		</figure>
	) : (
		image
	);
};

const imageWrapperStyles = css({
	position: 'relative',
	display: 'grid',
	flexGrow: 1,
	alignSelf: 'stretch',
	justifySelf: 'stretch',
	overflow: 'hidden',
	containerType: 'size',
	backgroundColor: wacToken('color.neutral.light0'),
});

const backgroundImageStyles = css({
	position: 'absolute',
	inset: token('space.0'),
	filter: 'blur(60px)',
	transform: 'scale(1.16)',
	pointerEvents: 'none',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'> img': {
		display: 'block',
		inlineSize: '100%',
		blockSize: '100%',
		objectFit: 'cover',
	},
});

const foregroundImageFrameStyles = css({
	position: 'relative',
	zIndex: 1,
	placeSelf: 'center',
	aspectRatio: '16 / 9',
	inlineSize: 'min(100cqw, calc(100cqh * 16 / 9))',
	blockSize: 'min(100cqh, calc(100cqw * 9 / 16))',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'> img': {
		display: 'block',
		inlineSize: '100%',
		blockSize: '100%',
		objectFit: 'cover',
	},
});

BlurredBackgroundImage.displayName = 'BlurredBackgroundImage';
